import {useMemo, useState} from 'react';
import type {HeadingData} from '@shopify/online-store-ui';
import {
  setHeading,
  createToolbarComponent,
  findHeadingType,
  HeadingType,
  RichTextEditorToolbar as Toolbar,
  ParagraphType,
  useRichTextSelection as useSelection,
} from '@shopify/online-store-ui';
import {classNames, variationName} from '@shopify/css-utilities';
import {useI18n} from '@shopify/react-i18n';
import {Popover, UnstyledButton} from '@shopify/polaris';

import {FormattingCheck} from './icons';
import styles from './FormattingList.scss';

const MIXED_SELECTION_CONTENT = ' ';

export const FormattingList = createToolbarComponent(
  HeadingType,
  ({editor, plugin: {type}, disabled}) => {
    const [active, setActive] = useState(false);
    const [i18n] = useI18n();
    const {blur, selection, setSelection, restoreSelection} =
      useSelection(editor);
    const activeTextLevel = findHeadingType(
      editor,
      editor.selection,
      ParagraphType,
    );

    const tooltip = disabled ? null : i18n.translate('formatting');
    const activatorContent = useMemo(() => {
      // check for mixed selection
      if (activeTextLevel === null) {
        return MIXED_SELECTION_CONTENT;
      }

      if (activeTextLevel === ParagraphType) {
        return i18n.translate('paragraph');
      }

      return i18n.translate('heading', {level: activeTextLevel});
    }, [activeTextLevel, i18n]);

    const activator = (
      <div className={styles.FormattingListActivator}>
        <Toolbar.Button
          disclosure
          tooltip={tooltip}
          type={type}
          onAction={() => {
            setSelection(selection);
            blur();
            setActive(true);
          }}
          content={activatorContent}
          disabled={disabled}
        />
      </div>
    );

    const textLevels: (HeadingData['level'] | null)[] = [
      null,
      1,
      2,
      3,
      4,
      5,
      6,
    ];

    const formatOptions = textLevels.map((textLevel) => {
      const onAction = () => {
        setHeading(editor, textLevel, ParagraphType);
        restoreSelection(selection);
        setActive(false);
      };

      const content = textLevel
        ? i18n.translate('heading', {level: textLevel})
        : i18n.translate('paragraph');

      return {
        content,
        onAction,
        active: content === activatorContent,
        element: textLevel ? `h${textLevel}` : 'p',
      };
    });

    return (
      <Popover
        active={active}
        activator={activator}
        onClose={() => {
          restoreSelection(selection);
          setActive(false);
        }}
      >
        <FormatsActionList options={formatOptions} />
      </Popover>
    );
  },
);

interface FormatOption {
  content: string;
  onAction: () => void;
  active: boolean;
  element: string;
}

function FormatsActionList({options}: {options: FormatOption[]}) {
  const listItems = options.map(({content, onAction, active, element}) => {
    const buttonClassName = classNames(
      styles.OptionButton,
      active && styles['OptionButton-selected'],
      styles[variationName('element', element)],
    );

    const checkMark = active && (
      <div className={styles.SelectedCheck}>
        <FormattingCheck />
      </div>
    );

    return (
      <li className={styles.Option} key={element}>
        <UnstyledButton
          className={buttonClassName}
          onClick={onAction}
          pressed={active}
        >
          {checkMark}
          {content}
        </UnstyledButton>
      </li>
    );
  });

  return <ul className={styles.FormattingList}>{listItems}</ul>;
}
